import React from 'react';
import { notification } from 'antd';
import type { NotificationProps } from '../../../atoms/Notification/useNotification';

export const AGENTIC_EXPORT_PROGRESS_NOTIFICATION_KEY = 'agentic-export-progress';

export type AgenticExportProgressFormat = 'docx' | 'xlsx' | 'pdf' | 'ppt';

const FORMAT_LABEL: Record<AgenticExportProgressFormat, string> = {
    docx: 'Word',
    xlsx: 'Excel',
    pdf: 'PDF',
    ppt: 'PPT',
};

const ExportProgressDescription: React.FC<{ 'data-test-id'?: string }> = ({ 'data-test-id': dataTestId }) => (
    <div className="mt-2 -mb-1" data-test-id={dataTestId}>
        {/* Same sweep animation as AgenticAnalysisSummaryLoading (gradient bar moves left → right) */}
        <style>{`
            @keyframes agentic-export-notification-sweep {
                0% { transform: translateX(-100%); }
                100% { transform: translateX(400%); }
            }
        `}</style>
        <div className="w-full h-2 rounded-sm overflow-hidden bg-[#e5e7eb]">
            <div
                className="h-full w-1/3 rounded-sm"
                style={{
                    background: 'linear-gradient(to right, #3b82f6, #06b6d4, #22c55e)',
                    animation: 'agentic-export-notification-sweep 2s ease-in-out infinite',
                }}
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label="Export in progress"
            />
        </div>
    </div>
);

/** Opens a non-modal notification (bottom-right when layout uses notification.config) with a progress bar. */
export function openAgenticExportProgressNotification(
    open: (args: NotificationProps) => void,
    format: AgenticExportProgressFormat,
    options?: { 'data-test-id'?: string },
): void {
    open({
        key: AGENTIC_EXPORT_PROGRESS_NOTIFICATION_KEY,
        message: `Exporting as ${FORMAT_LABEL[format]}`,
        description: <ExportProgressDescription data-test-id={options?.['data-test-id']} />,
        duration: 0,
        placement: 'bottomRight',
    });
}

export function closeAgenticExportProgressNotification(): void {
    notification.destroy(AGENTIC_EXPORT_PROGRESS_NOTIFICATION_KEY);
}

/** Ensures the LTR progress bar stays visible briefly even when export finishes instantly (sync client export or callback that omits `return promise`). Pass `openedAtMs` from `performance.now()` right after `openAgenticExportProgressNotification`. */
const MIN_AGENTIC_EXPORT_PROGRESS_VISIBLE_MS = 420;

export async function awaitMinimumDurationThenCloseAgenticExportProgress(openedAtMs: number): Promise<void> {
    const elapsed = performance.now() - openedAtMs;
    const remaining = MIN_AGENTIC_EXPORT_PROGRESS_VISIBLE_MS - elapsed;
    if (remaining > 0) {
        await new Promise<void>((resolve) => setTimeout(resolve, remaining));
    }
    closeAgenticExportProgressNotification();
}

/**
 * Yields until after the next paint so the progress notification (LTR sweep bar) can mount and
 * render. Word/PDF export “naturally” spans frames because the work is async; client-side Excel
 * is synchronous, so call this after openAgenticExportProgressNotification and before any
 * blocking xlsx work.
 */
export function awaitProgressNotificationPaint(): Promise<void> {
    return new Promise((resolve) => {
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                resolve();
            });
        });
    });
}
